/* ───────────────────────────────────────────────────────────────────────────
   cf-lead-capture.js — Click Fencing
   2026-08-26

   WHY THIS EXISTS
   Google Ads spends on this site and can see nothing. The account optimises
   toward "Purchase", an event that has never fired, because nobody impulse-buys
   a $3,700 glass kit online. The steps that DO happen — finishing a quote,
   asking us to work out a parts list — fired no event and, in the case of
   "Send us your plans", captured no lead at all: it opened a mailto draft that
   lands in no CRM and is invisible to Google and to Pete.

   WHAT IT DOES
   1. Repoints the floating "Send us your plans" pill at /pages/contact — the
      real form, which already takes attachments via the Supabase uploader.
      The mailto stays as a secondary option for people who prefer their own
      mail client.
   2. Adds "Email me this quote instead" to the quote-review page, next to
      "Continue to Payment", carrying the quote into the contact form.
   3. Fires four named events into the existing Google tag (AW-18355899206) on
      the four steps that actually happen.

   SAFETY
   Every hook is wrapped. Nothing here may break a calculator, the quote page
   or checkout. If an element is missing the feature quietly does not appear.
   ─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.__cfLeadCaptureInstalled) return;
  window.__cfLeadCaptureInstalled = true;

  /* ── Google Ads ──────────────────────────────────────────────────────────
     The tag is already on every page. These four events fire into it by name
     whether or not a conversion action exists yet — which is what lets Google
     Ads offer them later under "New conversion action → from a Google tag
     event". Fill a label in below once the matching action exists and that
     event starts counting as a conversion too. Empty label = event only.     */
  var AW = 'AW-18355899206';
  var LABELS = {
    // Created in Google Ads 27 Aug 2026, account 642-952-5948.
    cf_lead_submit:   'bDFeCPiFw-gcEMaW47BE',   // CF - Enquiry submitted     (PRIMARY, Submit lead form)
    cf_quote_ready:   'OLgoCK75uegcEMaW47BE',   // CF - Quote completed       (PRIMARY, Request quote)
    cf_quote_details: 'qIlbCPXFw-gcEMaW47BE',   // CF - Quote details submitted (secondary)
    cf_plans_open:    'wIBhCKnHw-gcEMaW47BE'    // CF - Plans / help requested  (secondary)
  };

  var QUOTE_KEY  = 'cf_quote_for_enquiry';
  var FIRED_KEY  = 'cf_fired_';

  function log() {
    try { console.log.apply(console, ['[cf-lead-capture]'].concat([].slice.call(arguments))); }
    catch (e) { /* ignore */ }
  }

  /* Fire once per session per event, so a refresh or a back-button never
     double-counts. Google de-duplicates purchases, not custom events.

     2026-08-27: the stamp is now written only AFTER the event has actually
     been handed to gtag. Writing it first is what silently lost every single
     cf_lead_submit: the stamp went down, gtag had not loaded yet, fire()
     bailed, and the stamp then blocked any retry for the rest of the session.
     Verified on the live contact page - ?contact_posted=true carried the
     stamp 'posted' while dataLayer held no cf_lead_submit at all. */
  function alreadyFired(name, stamp) {
    try { return sessionStorage.getItem(FIRED_KEY + name) === stamp; }
    catch (e) { return false; }   // private browsing - better to fire twice than never
  }
  function markFired(name, stamp) {
    try { sessionStorage.setItem(FIRED_KEY + name, stamp); } catch (e) { /* ignore */ }
  }

  /* gtag is injected asynchronously by Shopify's Google & YouTube channel and
     is NOT a function at DOMContentLoaded. Waiting for it is the difference
     between a conversion Google can see and one it cannot. */
  function whenGtag(cb) {
    if (typeof window.gtag === 'function') { cb(); return; }
    var waited = 0;
    var iv = setInterval(function () {
      if (typeof window.gtag === 'function') { clearInterval(iv); cb(); return; }
      waited += 100;
      if (waited >= 15000) { clearInterval(iv); log('gtag never loaded - event dropped'); }
    }, 100);
  }

  function fire(name, params, dedupeStamp) {
    try {
      if (dedupeStamp && alreadyFired(name, dedupeStamp)) { log('skipped (already fired):', name); return; }
      params = params || {};
      whenGtag(function () {
        try {
          if (dedupeStamp && alreadyFired(name, dedupeStamp)) return;
          if (dedupeStamp) markFired(name, dedupeStamp);

          window.gtag('event', name, params);

          var label = LABELS[name];
          if (label) {
            window.gtag('event', 'conversion', {
              send_to: AW + '/' + label,
              value: params.value || 0,
              currency: 'AUD'
            });
            log('fired', name, '+ conversion', params);
          } else {
            log('fired', name, '(no conversion label set yet)', params);
          }
        } catch (e) { log('fire failed', name, e); }
      });
    } catch (e) { log('fire failed', name, e); }
  }

  function txt(sel, root) {
    try {
      var el = (root || document).querySelector(sel);
      return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
    } catch (e) { return ''; }
  }

  function money(s) {
    var m = String(s || '').replace(/[, ]/g, '').match(/\$?([0-9]+(?:\.[0-9]{1,2})?)/);
    return m ? parseFloat(m[1]) : 0;
  }

  /* ─────────────────────────────────────────────────────────────────────────
     1. Contact form — fire on the success page
     Shopify redirects a successful submission to ?contact_posted=true. That
     is a real navigation, so this is the one signal that cannot be faked by a
     validation error or a half-filled form.
     ───────────────────────────────────────────────────────────────────────── */
  function contactSuccess() {
    try {
      if (location.search.indexOf('contact_posted=true') === -1) return;
      fire('cf_lead_submit', { lead_type: 'contact_form' }, 'posted');
      // Standard GA4-style lead event as well, so any future GA4 property sees it.
      whenGtag(function () {
        try { window.gtag('event', 'generate_lead', { currency: 'AUD', value: 0 }); } catch (e) {}
      });

      /* The theme DOES render "Thanks! We'll be in touch soon." - but it sits
         inside the form, ~770px down the page, and Shopify lands the visitor at
         the top with no scroll. Pete's words: "the enquiry page just reset after
         i sent it. it did not say thank you for your enquiry". It was there; he
         could not see it. Bring it to them and make it unmissable. */
      showThanks();
    } catch (e) { log('contactSuccess failed', e); }
  }

  function showThanks() {
    var tries = 0;
    var iv = setInterval(function () {
      try {
        var el = document.querySelector('.form-status');
        if (!el) { if (++tries > 60) clearInterval(iv); return; }
        clearInterval(iv);

        dressBanner(el);

        /* Scroll it OURSELVES rather than asking scrollIntoView.
           Two things defeat the obvious approach, both seen live on 27 Aug:
           - Shopify redirects to /pages/contact?contact_posted=true#ContactForm.
             The browser's own fragment jump lands on the FORM, which sits ~20px
             BELOW the banner, so the message ends up scrolled off the top. It
             also runs after DOMContentLoaded, so it overwrites anything we did.
           - The theme's <sticky-header> is 174px tall and overlays the top of
             the viewport, so "at the top of the screen" means "hidden".
           Measured target, then re-asserted twice to outlast the fragment jump
           and any late layout shift from lazy images. */
        parkBanner(el);
        setTimeout(function () { parkBanner(el); }, 700);
        setTimeout(function () { parkBanner(el); }, 1600);

        try { el.setAttribute('tabindex', '-1'); el.focus({ preventScroll: true }); } catch (e) {}

        log('thank-you message brought into view');
      } catch (e) { clearInterval(iv); log('showThanks failed', e); }
    }, 100);
  }

  function headerOffset() {
    try {
      var h = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
      var px = h ? h.getBoundingClientRect().height : 0;
      return (px || 0) + 24;
    } catch (e) { return 120; }
  }

  function parkBanner(el) {
    try {
      var abs = el.getBoundingClientRect().top + window.scrollY;
      var target = Math.max(0, abs - headerOffset());
      // Instant, NOT smooth. A smooth scroll here is cancelled every time -
      // measured live: scrollY stayed at the fragment's 770 through three
      // attempts, while the same call with the default instant behaviour
      // landed at 571 and held.
      window.scrollTo(0, target);
    } catch (e) {
      try { el.scrollIntoView(); } catch (e2) {}
    }
  }

  /* The theme's own success text is one small line of green type. Pete sent a
     real enquiry through it and reported "it did not say thank you for your
     enquiry" - so even in view it was not reading as a confirmation. Same
     words, given the weight of an actual receipt. */
  function dressBanner(el) {
    try {
      if (el.getAttribute('data-cf-dressed')) return;
      el.setAttribute('data-cf-dressed', '1');
      el.style.cssText += ';display:block;background:#eaf5ee;border:1px solid #1f6f3f;' +
        'border-left:5px solid #1f6f3f;border-radius:8px;padding:1.6rem 1.8rem;' +
        'margin:0 0 2rem;font-size:1.7rem;line-height:1.5;color:#123;';

      var extra = document.createElement('div');
      extra.style.cssText = 'font-size:1.4rem;line-height:1.55;margin-top:.6rem;opacity:.85;';
      extra.textContent = 'We have your enquiry and a copy is on its way to your inbox. ' +
                          'One of us will come back to you within one business day.';
      el.appendChild(extra);
    } catch (e) { log('dressBanner failed', e); }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     2. "Send us your plans" — point it at the form that works
     cf-send-plans.js builds the pill with a mailto as its primary action. We
     do not fight it for ownership; we wait for it, then swap the panel's
     actions. If that script is ever removed this simply never runs.
     ───────────────────────────────────────────────────────────────────────── */
  function upgradePlansPill() {
    var tries = 0;
    var iv = setInterval(function () {
      try {
        var box = document.querySelector('[data-cf-plans]');
        if (!box) { if (++tries > 100) clearInterval(iv); return; }
        clearInterval(iv);

        var primary = box.querySelector('.cf-plans__primary');
        var alt     = box.querySelector('.cf-plans__alt');
        var pill    = box.querySelector('.cf-plans__pill');
        if (!primary) return;

        var mailtoHref = primary.getAttribute('href') || '';

        // Primary now goes to the real form, which takes attachments.
        primary.setAttribute('href', '/pages/contact#ContactForm');
        primary.textContent = 'Send your plans →';
        primary.addEventListener('click', function () {
          fire('cf_plans_open', { lead_type: 'send_plans' });
        });

        // Keep the mail-client route for anyone who prefers it.
        if (alt) {
          alt.setAttribute('href', mailtoHref);
          alt.textContent = 'Or email us directly';
          alt.addEventListener('click', function () {
            fire('cf_plans_open', { lead_type: 'send_plans_mailto' });
          });
        }

        if (pill) {
          pill.addEventListener('click', function () {
            fire('cf_plans_open', { lead_type: 'send_plans_pill' });
          });
        }

        log('plans pill repointed at /pages/contact');
      } catch (e) { clearInterval(iv); log('upgradePlansPill failed', e); }
    }, 100);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     3. Quote review — fire when a real quote lands, and offer a way out that
        is not "pay now".
     ───────────────────────────────────────────────────────────────────────── */
  function quoteSummary() {
    var name  = txt('#qr-quote-name') || 'Pool fence quote';
    var total = txt('#qr-grand-total');
    var lines = [];
    try {
      var rows = document.querySelectorAll('#qr-summary li, #qr-summary tr, #qr-accordion li');
      for (var i = 0; i < rows.length && lines.length < 40; i++) {
        var t = (rows[i].textContent || '').replace(/\s+/g, ' ').trim();
        if (t) lines.push('  ' + t);
      }
    } catch (e) { /* ignore */ }
    return { name: name, total: total, lines: lines };
  }

  function onQuoteReview() {
    if (location.pathname.indexOf('/pages/quote-review') === -1) return;

    var tries = 0;
    var iv = setInterval(function () {
      try {
        var totalEl = document.getElementById('qr-grand-total');
        var raw     = totalEl ? (totalEl.textContent || '').trim() : '';
        var value   = money(raw);

        // "—" is the empty state. Wait for a real number.
        if (!value) { if (++tries > 300) clearInterval(iv); return; }
        clearInterval(iv);

        var q = quoteSummary();
        fire('cf_quote_ready', { value: value, currency: 'AUD', quote_name: q.name }, q.name + '|' + raw);

        addEmailQuoteButton(q, value);
        hookDetailsForm(value);
      } catch (e) { clearInterval(iv); log('onQuoteReview failed', e); }
    }, 100);
  }

  function addEmailQuoteButton(q, value) {
    try {
      var actions = document.querySelector('.qr-cta__actions');
      if (!actions || actions.querySelector('[data-cf-email-quote]')) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'qr-btn qr-btn--ghost';
      btn.setAttribute('data-cf-email-quote', '');
      btn.textContent = 'Email me this quote instead';
      btn.style.marginTop = '.8rem';

      btn.addEventListener('click', function () {
        try {
          var body = [
            'Please check this quote and send me a fixed price.',
            '',
            'Quote: ' + q.name,
            'Estimated total: ' + (q.total || '—'),
            ''
          ];
          if (q.lines.length) body.push('Breakdown as shown on the site:', q.lines.join('\n'), '');
          body.push('My questions / anything unusual about the site:');
          body.push('');

          sessionStorage.setItem(QUOTE_KEY, JSON.stringify({ body: body.join('\n'), total: q.total }));
        } catch (e) { /* prefill is a nicety, never a blocker */ }

        fire('cf_plans_open', { lead_type: 'email_my_quote', value: value, currency: 'AUD' });
        location.href = '/pages/contact#ContactForm';
      });

      // Sit directly under "Continue to Payment", never after "Delete Quote".
      var payBtn = document.getElementById('qr-enquire-btn');
      if (payBtn && payBtn.parentNode === actions && payBtn.nextSibling) {
        actions.insertBefore(btn, payBtn.nextSibling);
      } else {
        actions.appendChild(btn);
      }
      log('added "Email me this quote instead"');
    } catch (e) { log('addEmailQuoteButton failed', e); }
  }

  /* The details modal is the last step before checkout. Someone who fills in
     name, email and phone has declared themselves a lead whether or not they
     then pay, so this is worth counting separately from a purchase. */
  function hookDetailsForm(value) {
    try {
      var form = document.getElementById('qr-enquire-form');
      if (!form || form.__cfHooked) return;
      form.__cfHooked = true;

      form.addEventListener('submit', function () {
        try {
          var email = (document.getElementById('qr-f-email') || {}).value || '';
          if (!email) return;                       // validation will stop it anyway
          fire('cf_quote_details', { value: value, currency: 'AUD' }, email + '|' + value);
        } catch (e) { /* ignore */ }
      }, true);

      log('details form hooked');
    } catch (e) { log('hookDetailsForm failed', e); }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     4. Contact page — drop the quote into the message box if one is waiting.
     ───────────────────────────────────────────────────────────────────────── */
  function prefillContact() {
    try {
      if (location.pathname.indexOf('/pages/contact') === -1) return;

      var raw = sessionStorage.getItem(QUOTE_KEY);
      if (!raw) return;
      sessionStorage.removeItem(QUOTE_KEY);

      var data = JSON.parse(raw);
      var box  = document.getElementById('ContactForm-body');
      if (!box) return;

      if (!box.value.trim()) box.value = data.body;
      try { box.focus(); box.setSelectionRange(box.value.length, box.value.length); } catch (e) {}

      var note = document.createElement('p');
      note.textContent = 'Your quote' + (data.total ? ' (' + data.total + ')' : '') +
                         ' has been added to the message below. Attach your plans if you have them.';
      note.style.cssText = 'font-size:1.35rem;line-height:1.5;margin:0 0 1.2rem;padding:1rem 1.2rem;' +
                           'border-left:3px solid currentColor;opacity:.85;';
      var field = box.closest('.field') || box.parentNode;
      if (field && field.parentNode) field.parentNode.insertBefore(note, field);

      log('quote prefilled into the contact form');
    } catch (e) { log('prefillContact failed', e); }
  }

  /* ───────────────────────────────────────────────────────────────────────
     5. Mirror the enquiry into the CRM.
     Shopify's native contact form does exactly one thing: email the store
     contact address. It creates no contact, no deal, no record - which is why
     Click Fencing had two contacts in the CRM and an empty Site Enquiries
     screen while real enquiries sat in a mailbox and nowhere else.

     This copies the submission to the click-enquiry endpoint, which writes the
     contact (source 'Website Enquiry Form'), opens a deal on the pipeline and
     sends the customer an acknowledgement. Shopify's own email still goes out
     as before - nothing is replaced, only added.

     sendBeacon, because the page navigates away the instant this returns. A
     text/plain blob keeps it a CORS "simple request", so there is no preflight
     to fail mid-unload. Wrapped end to end: if this throws, the customer's
     enquiry still reaches Shopify exactly as it does today.
     ──────────────────────────────────────────────────────────────────────── */
  var CRM_ENQUIRY_ENDPOINT = 'https://mhzmtafzxeshqqtktbwh.supabase.co/functions/v1/click-enquiry';

  function val(id) {
    try { var el = document.getElementById(id); return el ? String(el.value || '').trim() : ''; }
    catch (e) { return ''; }
  }


  /* 2026-09-27 - the one "I'm not a robot" tick box, and a honeypot bots fill in and people never see. */
  function addHumanCheck(form) {
    try {
      if (form.querySelector('#cf-human')) return;
      var btn = form.querySelector('button[type="submit"],input[type="submit"]');
      if (!btn) return;
      var st = document.createElement('style');
      st.textContent = '.cf-human{display:flex;align-items:center;gap:10px;margin:14px 0 12px;padding:12px 14px;' +
        'border:1px solid #cfd6dd;border-radius:8px;background:#fff;color:#1c2228;font-size:15px;font-weight:600;' +
        'cursor:pointer;max-width:320px}.cf-human input{width:22px;height:22px;margin:0;accent-color:#e60000;cursor:pointer}' +
        '.cf-human-msg{color:#b30000;font-size:14px;margin:0 0 10px}.cf-hp{position:absolute!important;left:-9999px!important;' +
        'width:1px;height:1px;opacity:0}.cf-sent{padding:22px 20px;border-radius:10px;background:#eaf7ef;border:1px solid #9fd5b3;' +
        'color:#14532d;font-size:16px;line-height:1.5}.cf-sent strong{display:block;font-size:19px;margin-bottom:6px}';
      document.head.appendChild(st);
      var hp = document.createElement('input');
      hp.type = 'text'; hp.name = 'cf_company'; hp.className = 'cf-hp'; hp.tabIndex = -1;
      hp.setAttribute('autocomplete', 'off'); hp.setAttribute('aria-hidden', 'true');
      var lab = document.createElement('label');
      lab.className = 'cf-human';
      lab.innerHTML = '<input type="checkbox" id="cf-human" required> <span>I’m not a robot</span>';
      var host = btn.parentNode;
      host.insertBefore(hp, host.firstChild);
      host.insertBefore(lab, btn);
    } catch (e) { log('addHumanCheck failed', e); }
  }

  function humanMsg(form, text) {
    try {
      var m = form.querySelector('.cf-human-msg');
      if (!m) {
        m = document.createElement('p'); m.className = 'cf-human-msg';
        var lab = form.querySelector('.cf-human');
        lab.parentNode.insertBefore(m, lab);
      }
      m.textContent = text;
    } catch (e) {}
  }

  function showSent(form) {
    try {
      var box = document.createElement('div');
      box.className = 'cf-sent';
      box.setAttribute('role', 'status'); box.setAttribute('tabindex', '-1');
      box.innerHTML = '<strong>Thanks — we’ve got your message.</strong>' +
        'We’ll come back to you within one business day. A copy is on its way to your inbox.';
      form.parentNode.replaceChild(box, form);
      parkBanner(box);
      try { box.focus({ preventScroll: true }); } catch (e) {}
    } catch (e) { log('showSent failed', e); }
  }

  function mirrorContactForm() {
    try {
      /* 2026-09-13 - ALSO the homepage "Send us your plans" form (sections/cf-home-sell.liquid).
         It is found by its [data-cf-mirror] wrapper, never by action: the footer newsletter form
         posts to /contact too and must never become a CRM enquiry. */
      var marked = document.querySelector('[data-cf-mirror] form');
      if (!marked && location.pathname.indexOf('/pages/contact') === -1) return;
      var form = marked || document.querySelector('form[action*="/contact"]');
      if (!form || form.__cfMirrored) return;
      form.__cfMirrored = true;

      /* 2026-09-27 (Pete, after Michelle Jovanovic spent five rounds on Shopify's picture puzzles):
         "lets just put the button to click that says i am not a robot instead of all the rest of the
         games ... make sure all the email routes are the same on the website".
         The form no longer posts to Shopify's /contact (that POST is what triggers hCaptcha). It sends
         ONLY to click-enquiry, which files the CRM contact, emails the team at enquiries@ and sends
         the customer's acknowledgement - the one route every Click form uses. Bots are stopped by a
         required "I'm not a robot" tick box and a hidden honeypot field. If the CRM call fails, the
         form falls back to the native Shopify post so an enquiry is never lost. */
      addHumanCheck(form);

      /* 27 Sep, second pass: Shopify's own spam script listens for the SUBMIT event and calls
         form.submit() itself before any theme listener runs (measured: two native submits logged
         ahead of our capture handler) - that is what sends people to /challenge. So the Send
         button is no longer a submit button. Its click, and Enter in a field, run send() directly;
         no submit event ever fires, so Shopify's script never sees one. */
      var sendBtn = form.querySelector('button[type="submit"],input[type="submit"]');
      if (sendBtn) sendBtn.setAttribute('type', 'button');
      var busy = false;

      function send() {
        try {
          if (busy) return;
          if (form.querySelector('li.is-uploading')) {
            humanMsg(form, 'Please wait — your file is still uploading.');
            return;
          }
          if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
          var name  = val('ContactForm-name');
          var email = val('ContactForm-email');
          var phone = val('ContactForm-phone');
          if (!name || (!email && !phone)) { humanMsg(form, 'Please add your name and an email or phone number.'); return; }
          var tick = form.querySelector('#cf-human');
          if (tick && !tick.checked) { humanMsg(form, 'Please tick “I’m not a robot”, then press send.'); return; }

          var hp = form.querySelector('input[name="cf_company"]');
          if (hp && hp.value) { showSent(form); log('honeypot tripped - dropped'); return; }

          var raw = val('ContactForm-attachments');
          var attachments = raw ? raw.split(/[\n,]+/).map(function (u) { return u.trim(); })
                                   .filter(function (u) { return u.length; }) : [];
          var payload = {
            name: name, email: email, phone: phone,
            interest: val('ContactForm-interest'),
            suburb: val('ContactForm-suburb'),
            message: val('ContactForm-body'),
            attachments: attachments,
            page: (marked ? 'Homepage - Send us your plans' : (document.title || 'Contact - Click Fencing'))
          };

          busy = true;
          var btnText = sendBtn ? sendBtn.innerHTML : '';
          if (sendBtn) { sendBtn.disabled = true; sendBtn.innerHTML = 'Sending…'; }
          var done = false;
          fetch(CRM_ENQUIRY_ENDPOINT, {
            method: 'POST', body: JSON.stringify(payload), keepalive: true,
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, mode: 'cors'
          }).then(function (r) {
            if (!r.ok) throw new Error('click-enquiry ' + r.status);
            done = true;                                   // from here on, never fall back
            log('enquiry sent to the CRM (enquiries@)');
            try { fire('cf_lead_submit', { lead_type: 'contact_form' }, 'crm-' + Date.now()); } catch (x) {}
            try { whenGtag(function () { try { window.gtag('event', 'generate_lead', { currency: 'AUD', value: 0 }); } catch (y) {} }); } catch (x) {}
            showSent(form);
          }).catch(function (err) {
            if (done) return;
            log('CRM send failed - falling back to Shopify', err);
            busy = false;
            if (sendBtn) { sendBtn.disabled = false; sendBtn.innerHTML = btnText; sendBtn.setAttribute('type', 'submit'); }
            form.__cfNative = true;
            HTMLFormElement.prototype.submit.call(form);
          });
        } catch (x) { busy = false; log('send failed', x); }
      }

      if (sendBtn) sendBtn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopImmediatePropagation(); send();
      }, true);
      form.addEventListener('keydown', function (e) {
        var t = e.target || {};
        if (e.key === 'Enter' && t.tagName !== 'TEXTAREA' && t.tagName !== 'BUTTON') { e.preventDefault(); send(); }
      }, true);
      form.addEventListener('submit', function (e) {
        if (form.__cfNative) return;
        e.preventDefault(); e.stopImmediatePropagation(); send();
      }, true);

      log('contact form mirroring armed');
    } catch (e) { log('mirrorContactForm failed', e); }
  }

  /* ── start ─────────────────────────────────────────────────────────────── */
  function start() {
    contactSuccess();
    prefillContact();
    mirrorContactForm();
    upgradePlansPill();
    onQuoteReview();
    log('installed');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
/* 2026-09-13 suburb sent as its own field (click-enquiry v7). Re-pushed to refresh a stale CDN copy. */
